import axios from 'axios';
import {
  BillingInfo,
  CheckoutSession,
  PortalSession,
  ScheduledDowngrade,
  BillingCycle,
  PlanType,
  PlanConfiguration,
  SubscriptionChangePreview,
} from '../types/billing';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

class BillingService {
  private getAuthHeaders() {
    return getAuthHeaders();
  }

  async getBillingLimits(organizationId: string): Promise<BillingInfo> {
    const response = await axios.get(
      `${API_URL}/billing/limits?organization_id=${organizationId}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async getAvailablePlans(): Promise<PlanConfiguration[]> {
    const response = await axios.get(`${API_URL}/billing/plans`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async startTrial(
    organizationId: string,
    trialDays?: number,
  ): Promise<{
    message: string;
    trial_end_date?: string;
    billing_info: BillingInfo;
  }> {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (trialDays) {
      params.append('trial_days', trialDays.toString());
    }

    const response = await axios.post(
      `${API_URL}/billing/start-trial?${params}`,
      {},
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async createPortalSession(
    organizationId: string,
    returnUrl: string,
  ): Promise<PortalSession> {
    const response = await axios.post(
      `${API_URL}/billing/portal-session`,
      {},
      {
        params: {
          organization_id: organizationId,
          return_url: returnUrl,
        },
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async createSubscription(
    organizationId: string,
    planType: PlanType,
    billingCycle: BillingCycle,
    editorCount: number,
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<CheckoutSession> {
    const params = new URLSearchParams({
      organization_id: organizationId,
      plan_type: planType,
      billing_cycle: billingCycle,
      editor_count: editorCount.toString(),
    });

    if (successUrl) {
      params.append('success_url', successUrl);
    }
    if (cancelUrl) {
      params.append('cancel_url', cancelUrl);
    }

    const response = await axios.post(
      `${API_URL}/billing/subscribe?${params}`,
      {},
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async upgradeSubscription(
    organizationId: string,
    planType: PlanType,
    billingCycle: BillingCycle,
    editorCount: number,
  ): Promise<{ success: boolean; message: string; plan_type: string }> {
    const params = new URLSearchParams({
      organization_id: organizationId,
      plan_type: planType,
      billing_cycle: billingCycle,
      editor_count: editorCount.toString(),
    });

    const response = await axios.post(
      `${API_URL}/billing/upgrade?${params}`,
      {},
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async scheduleDowngrade(
    organizationId: string,
    newPlan: PlanType,
    editorCount?: number,
    newBillingCycle?: BillingCycle,
    force = false,
  ): Promise<
    | ScheduledDowngrade
    | {
        status: string;
        message: string;
        existing_change?: any;
        proposed_change?: any;
        requires_confirmation?: boolean;
      }
  > {
    const params: any = {
      organization_id: organizationId,
      new_plan: newPlan,
      force: force,
    };

    if (editorCount !== undefined) {
      params.editor_count = editorCount;
    }

    if (newBillingCycle) {
      params.new_billing_cycle = newBillingCycle;
    }

    const response = await axios.post(
      `${API_URL}/billing/schedule-downgrade`,
      {},
      {
        params,
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async updateEditorCount(
    organizationId: string,
    editorCount: number,
  ): Promise<{
    message: string;
    editor_count: number;
    subscription_id: string;
    new_monthly_cost: number;
    currency: string;
    next_billing_date: string;
  }> {
    const response = await axios.post(
      `${API_URL}/billing/update-editors?organization_id=${organizationId}&editor_count=${editorCount}`,
      {},
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async changeBillingCycle(
    organizationId: string,
    newCycle: BillingCycle,
    force = false,
  ): Promise<
    | {
        message: string;
        effective_date: string;
        current_cycle: string;
        new_cycle: string;
      }
    | {
        status: string;
        message: string;
        existing_change?: any;
        proposed_change?: any;
        requires_confirmation?: boolean;
      }
  > {
    const params = new URLSearchParams({
      organization_id: organizationId,
      new_cycle: newCycle,
      force: force.toString(),
    });

    const response = await axios.post(
      `${API_URL}/billing/change-billing-cycle?${params}`,
      {},
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async cancelScheduledChange(organizationId: string): Promise<{
    success: boolean;
    message: string;
    subscription_id: string;
    current_plan: string;
  }> {
    const response = await axios.post(
      `${API_URL}/billing/cancel-scheduled-change?organization_id=${organizationId}`,
      {},
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async cancelSubscription(
    organizationId: string,
    atPeriodEnd = true,
  ): Promise<{
    success: boolean;
    message: string;
    subscription_id: string;
    cancel_at?: number;
    canceled_at?: number;
    immediate: boolean;
  }> {
    const response = await axios.post(
      `${API_URL}/billing/cancel-subscription`,
      {},
      {
        params: {
          organization_id: organizationId,
          at_period_end: atPeriodEnd,
        },
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async reactivateSubscription(organizationId: string): Promise<{
    success: boolean;
    message: string;
    subscription_id: string;
    status: string;
  }> {
    const response = await axios.post(
      `${API_URL}/billing/reactivate-subscription`,
      {},
      {
        params: {
          organization_id: organizationId,
        },
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async reconcileEditorCount(organizationId: string): Promise<{
    status: string;
    message: string;
    actual_editors?: number;
    stripe_quantity?: number;
    previous_stripe_quantity?: number;
    new_monthly_cost?: number;
    currency?: string;
    available_seats?: number;
  }> {
    const response = await axios.post(
      `${API_URL}/billing/reconcile-editors?organization_id=${organizationId}`,
      {},
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  async getPricing(): Promise<
    {
      plan_type: string;
      monthly_base_price: number | null;
      annual_base_price: number | null;
      monthly_editor_price: number | null;
      annual_editor_price: number | null;
      currency: string;
    }[]
  > {
    const response = await axios.get(`${API_URL}/billing/pricing`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async previewSubscriptionChange(
    organizationId: string,
    planType?: PlanType,
    billingCycle?: BillingCycle,
    editorCount?: number,
  ): Promise<SubscriptionChangePreview> {
    const params = new URLSearchParams({
      organization_id: organizationId,
    });

    if (planType) {
      params.append('plan_type', planType);
    }
    if (billingCycle) {
      params.append('billing_cycle', billingCycle);
    }
    if (editorCount !== undefined) {
      params.append('editor_count', editorCount.toString());
    }

    const response = await axios.post(
      `${API_URL}/billing/preview-change?${params}`,
      {},
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data;
  }

  // Helper method to format price for display
  formatPrice(priceInPence: number, currency = 'GBP'): string {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    });
    return formatter.format(priceInPence / 100);
  }

  // Helper method to calculate days remaining in trial
  calculateTrialDaysRemaining(trialEndDate?: string): number {
    if (!trialEndDate) return 0;

    const end = new Date(trialEndDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  }

  // Helper method to get plan display name
  getPlanDisplayName(planType: string): string {
    const planNames: Record<string, string> = {
      free: 'Free',
      starter: 'Starter',
      professional: 'Professional',
      enterprise: 'Enterprise',
    };
    return planNames[planType] || planType;
  }

  // Helper method to get plan color for UI
  getPlanColor(planType: string): string {
    const planColors: Record<string, string> = {
      free: 'gray',
      starter: 'blue',
      professional: 'purple',
      enterprise: 'green',
    };
    return planColors[planType] || 'gray';
  }
}

export const billingService = new BillingService();
